import { type ChatMessagePayload, type LawPayload, type LegalSourcePayload } from './ai/types.ts'
import {
	formatLawArticleReferences,
	lawPayloadMatchesLooseReference,
	LOOSE_LAW_BRACKET_CITATION_PATTERN_SOURCE,
	LOOSE_LAW_ID_BRACKET_CITATION_PATTERN_SOURCE,
	normalizeLawArticleForMatch,
	parseLooseLawBracketCitationParts,
	type LooseLawBracketCitation,
	parseLooseLawDocumentReference,
} from './legal-ai/countries/costa-rica/document-citation-tags.ts'
import {
	cgrDocumentAsTitle,
	cleanUrlOfLaw,
	costaRicaLegalSourceCitationDefinitions,
	type CostaRicaLegalSourceCitationDefinitionAny,
	findCostaRicaLegalPayloadByDocumentId,
	findCostaRicaLegalSourcePayload,
	getCostaRicaLegalSourceCitationByCitationType,
	getCostaRicaLegalSourcePayloads,
	getCgrDocumentUrl,
	getNexusUrl,
	getTraDocumentUrl,
	lawDocumentAsTitle,
	resolutionNumberAsTitle,
	traDocumentAsTitle,
} from './legal-ai/countries/costa-rica/sources/citations.ts'

export {
	cgrDocumentAsTitle,
	cleanUrlOfLaw,
	costaRicaLegalSourceCitationDefinitions,
	findCostaRicaLegalPayloadByDocumentId,
	findCostaRicaLegalSourcePayload,
	getCostaRicaLegalSourceCitationByCitationType,
	getCostaRicaLegalSourcePayloads,
	getCgrDocumentUrl,
	getNexusUrl,
	getTraDocumentUrl,
	lawDocumentAsTitle,
	resolutionNumberAsTitle,
	traDocumentAsTitle,
}

type ReplaceFunctions = {
	legalSource: (source: CostaRicaLegalSourceCitationDefinitionAny, payload: LegalSourcePayload) => string
	[sourceId: `source:${string}`]: (payload: LegalSourcePayload) => string
	projectFile: (projectFilePayload: NonNullable<ChatMessagePayload['projectFiles']>[number]) => string
	knowledgeBaseFile: (knowledgeBaseFilePayload: NonNullable<ChatMessagePayload['knowledgeBaseFiles']>[number]) => string
	projectLawCase: (projectLawCasePayload: NonNullable<ChatMessagePayload['projectResources']>[number]) => string
	projectMemory: (projectMemoryPayload: NonNullable<ChatMessagePayload['projectMemories']>[number]) => string
}

export const standaloneLegalDocumentIdPatternSource =
	'(?<![/"=])(?:ext-\\d-\\d{4}-\\d{4,7}|sen-\\d-\\d{4}-\\d{4,7}|act-\\d-\\d{4}-\\d{4,7}(?:-\\d+)?)'

export function groupLegalDocumentReferencesUnderContainerTag(text: string) {
	const documentTagPatternSource = '<document\\b[^>]*\\/?>'
	const standaloneDocumentIdPatternSource =
		'(?:ext-\\d-\\d{4}-\\d{4,7}|sen-\\d-\\d{4}-\\d{4,7}|act-\\d-\\d{4}-\\d{4,7}(?:-\\d+)?)'
	const documentSeparatorPatternSource = '(?:\\s*,\\s*(?:y\\s+)?|\\s+y\\s+)'
	const documentTagListPattern = new RegExp(
		`${documentTagPatternSource}(?:${documentSeparatorPatternSource}${documentTagPatternSource})+`,
		'g',
	)
	const standaloneIdListPattern = new RegExp(
		`${standaloneDocumentIdPatternSource}(?:${documentSeparatorPatternSource}${standaloneDocumentIdPatternSource})+`,
		'g',
	)
	const standaloneIdPattern = new RegExp(standaloneDocumentIdPatternSource, 'g')

	return text
		.replace(documentTagListPattern, match => {
			const normalizedMatch = match.replace(/,\s*$/, '').trim()
			return `<DocumentContainer>${normalizedMatch}</DocumentContainer>`
		})
		.replace(standaloneIdListPattern, match => {
			const normalizedMatch = match
				.replace(/,\s*$/, '')
				.trim()
				.replace(standaloneIdPattern, documentId => `<document id="${documentId}" />`)
			return `<DocumentContainer>${normalizedMatch}</DocumentContainer>`
		})
}

const DefaultReplaceFunctions: ReplaceFunctions = {
	legalSource: formatCostaRicaLegalSourceMarkdown,
	projectFile: projectFilePayload =>
		`[${projectFilePayload.fileName}](${getProjectFileResourceUrl(projectFilePayload.resourceId)})`,
	knowledgeBaseFile: knowledgeBaseFilePayload =>
		`[${knowledgeBaseFilePayload.fileName}](${getKnowledgeBaseFileResourceUrl(knowledgeBaseFilePayload.resourceId)})`,
	projectLawCase: projectLawCasePayload =>
		`[${projectLawCasePayload.title || projectLawCasePayload.label || 'Expediente'}](${getProjectLawCaseUrl(projectLawCasePayload.lawCaseId || '')})`,
	projectMemory: projectMemoryPayload =>
		`[${projectMemoryPayload.title || 'Memoria del cliente'}](${getProjectMemoryUrl(projectMemoryPayload.projectId, projectMemoryPayload.memoryId)})`,
}

function formatCostaRicaLegalSourceMarkdown(
	source: CostaRicaLegalSourceCitationDefinitionAny,
	payload: LegalSourcePayload,
) {
	return `[${source.citation.title(payload as never)}](${source.citation.url(payload as never)})`
}

export function getProjectFileResourceUrl(resourceId: string) {
	return `/ai/resources/project-file/${encodeURIComponent(resourceId)}`
}

export function getProjectLawCaseUrl(lawCaseId: string) {
	return `/expedientes/${encodeURIComponent(lawCaseId)}/resumen`
}

export function getKnowledgeBaseFileResourceUrl(resourceId: string) {
	return `/conocimiento/resources/knowledge-base-file?resourceId=${encodeURIComponent(resourceId)}`
}

export function getProjectMemoryUrl(projectId: string, memoryId?: string | null) {
	const projectPath = `/projects/${encodeURIComponent(projectId)}`
	if (!memoryId) return projectPath
	const encodedMemoryId = encodeURIComponent(memoryId)
	return `${projectPath}?memoryId=${encodedMemoryId}#project-memory-${encodedMemoryId}`
}

function getReplacementForDocumentId(
	documentId: string,
	payloads: ChatMessagePayload | null,
	replaceFunctions: Partial<ReplaceFunctions>,
) {
	if (!payloads) {
		return ''
	}

	const foundLegalPayload = findCostaRicaLegalPayloadByDocumentId(payloads, documentId)
	if (foundLegalPayload) {
		const sourceReplacementFunction = replaceFunctions[`source:${foundLegalPayload.source.id}`]
		if (sourceReplacementFunction) return sourceReplacementFunction(foundLegalPayload.payload)
		const replacementFunction = replaceFunctions.legalSource || DefaultReplaceFunctions.legalSource
		return replacementFunction(foundLegalPayload.source, foundLegalPayload.payload)
	}

	const looseLawPayload = findLawPayloadByLooseDocumentReference(payloads, documentId)
	if (looseLawPayload) {
		const lawSource = getCostaRicaLegalSourceCitationByCitationType('Ley')
		if (lawSource) {
			const sourceReplacementFunction = replaceFunctions[`source:${lawSource.id}`]
			if (sourceReplacementFunction) return sourceReplacementFunction(looseLawPayload)
			const replacementFunction = replaceFunctions.legalSource || DefaultReplaceFunctions.legalSource
			return replacementFunction(lawSource, looseLawPayload)
		}
	}

	const foundProjectFile = payloads.projectFiles?.find(p => p.resourceId === documentId)
	if (foundProjectFile) {
		return (replaceFunctions.projectFile || DefaultReplaceFunctions.projectFile)(foundProjectFile)
	}

	const foundKnowledgeBaseFile = payloads.knowledgeBaseFiles?.find(p => p.resourceId === documentId)
	if (foundKnowledgeBaseFile) {
		return (replaceFunctions.knowledgeBaseFile || DefaultReplaceFunctions.knowledgeBaseFile)(foundKnowledgeBaseFile)
	}

	const foundProjectResource = payloads.projectResources?.find(p => p.resourceId === documentId)
	if (foundProjectResource?.resourceType === 'law-case') {
		return (replaceFunctions.projectLawCase || DefaultReplaceFunctions.projectLawCase)(foundProjectResource)
	}

	const foundProjectMemory = payloads.projectMemories?.find(p => p.memoryId === documentId)
	if (foundProjectMemory) {
		return (replaceFunctions.projectMemory || DefaultReplaceFunctions.projectMemory)(foundProjectMemory)
	}

	return ''
}

function findLawPayloadByLooseDocumentReference(payloads: ChatMessagePayload, documentId: string) {
	const lawSource = getCostaRicaLegalSourceCitationByCitationType('Ley')
	const reference = parseLooseLawDocumentReference(documentId)
	if (!lawSource || !reference) return null

	const lawPayloads = getCostaRicaLegalSourcePayloads(payloads, lawSource) as Array<
		Omit<LawPayload, 'content'> & { id: string | number }
	>
	const matchingArticle = lawPayloads.find(payload => lawPayloadMatchesLooseReference(payload, reference))
	if (matchingArticle) return matchingArticle

	return (
		lawPayloads.find(payload => {
			const payloadLawNumber = String(payload.number ?? '').trim()
			return (
				payloadLawNumber === reference.lawNumber ||
				String(payload.idDocument ?? '').startsWith(`${reference.lawNumber}-`)
			)
		}) ?? null
	)
}

function lawPayloadMatchesBracketCitation(
	payload: Pick<LawPayload, 'idDocument' | 'article_number'>,
	citation: LooseLawBracketCitation,
) {
	if (String(payload.idDocument ?? '').trim() !== citation.documentId) return false
	const payloadArticle = normalizeLawArticleForMatch(payload.article_number)
	return Boolean(payloadArticle && citation.articles.map(normalizeLawArticleForMatch).includes(payloadArticle))
}

function findLawPayloadByBracketCitation(payloads: ChatMessagePayload, citation: LooseLawBracketCitation) {
	const lawSource = getCostaRicaLegalSourceCitationByCitationType('Ley')
	if (!lawSource) return null

	const lawPayloads = getCostaRicaLegalSourcePayloads(payloads, lawSource) as Array<
		Omit<LawPayload, 'content'> & { id: string | number }
	>
	return (
		lawPayloads.find(payload => lawPayloadMatchesBracketCitation(payload, citation)) ??
		lawPayloads.find(payload => String(payload.idDocument ?? '').trim() === citation.documentId) ??
		null
	)
}

function findLawPayloadByDocumentId(payloads: ChatMessagePayload, documentId: string) {
	const lawSource = getCostaRicaLegalSourceCitationByCitationType('Ley')
	if (!lawSource) return null

	const lawPayloads = getCostaRicaLegalSourcePayloads(payloads, lawSource) as Array<
		Omit<LawPayload, 'content'> & { id: string | number }
	>
	return lawPayloads.find(payload => String(payload.idDocument ?? '').trim() === documentId) ?? null
}

function replaceLooseLawBracketCitationsWithLinks(
	message: string,
	payloads: ChatMessagePayload | null,
	replaceFunctions: Partial<ReplaceFunctions>,
	idsFoundInDocTags: string[],
) {
	if (!payloads || !message.includes('[')) return message

	const bracketCitationWithOptionalTagPattern = new RegExp(
		`${LOOSE_LAW_BRACKET_CITATION_PATTERN_SOURCE}(\\s*<document\\b[^>]*\\bid=["']([^"']+)["'][^>]*\\/?>)?`,
		'gi',
	)

	return message.replace(
		bracketCitationWithOptionalTagPattern,
		(match, documentId: string, articleText: string, adjacentTag: string | undefined) => {
			const citation = parseLooseLawBracketCitationParts(documentId, articleText)
			if (!citation) return match

			const payload = findLawPayloadByBracketCitation(payloads, citation)
			if (!payload) return match

			const adjacentDocumentId = /\bid=(["'])([^"']+)\1/i.exec(adjacentTag ?? '')?.[2]
			if (adjacentTag && adjacentDocumentId !== payload.idDocument) return match

			const replacementLink = getReplacementForDocumentId(payload.idDocument, payloads, replaceFunctions)
			if (!replacementLink) return match

			idsFoundInDocTags.push(payload.idDocument)
			return `${formatLawArticleReferences(citation.articles)} ${replacementLink}`
		},
	)
}

function replaceLooseLawIdBracketCitationsWithLinks(
	message: string,
	payloads: ChatMessagePayload | null,
	replaceFunctions: Partial<ReplaceFunctions>,
	idsFoundInDocTags: string[],
) {
	if (!payloads || !message.includes('[')) return message

	const bracketIdWithOptionalDocumentTagPattern = new RegExp(
		`${LOOSE_LAW_ID_BRACKET_CITATION_PATTERN_SOURCE}(\\s*<document\\b[^>]*\\bid=["']([^"']+)["'][^>]*\\/?>)?(?!\\()`,
		'gi',
	)

	return message.replace(bracketIdWithOptionalDocumentTagPattern, (match, documentId: string, adjacentTag?: string) => {
		const payload = findLawPayloadByDocumentId(payloads, documentId)
		if (!payload) return match

		const adjacentDocumentId = /\bid=(["'])([^"']+)\1/i.exec(adjacentTag ?? '')?.[2]
		if (adjacentTag && adjacentDocumentId !== payload.idDocument) return match

		const replacementLink = getReplacementForDocumentId(payload.idDocument, payloads, replaceFunctions)
		if (!replacementLink) return match

		idsFoundInDocTags.push(payload.idDocument)
		return replacementLink
	})
}

export function replaceDocumentWithLinksInLines(
	message: string,
	payloads: ChatMessagePayload | null,
	replaceFunctions: Partial<ReplaceFunctions> = DefaultReplaceFunctions,
) {
	let processedMessage = message
	const idsFoundInDocTags: string[] = [] // IDs from <document> tags
	const idsFoundStandalone: string[] = [] // IDs found as standalone patterns

	// --- Pass 1: Handle <document ... /> tags ---
	const documentRegex = /<document[^>]*id="([^"]*)"[^>]*(\/?)>|<document[^>]*(\/?)>/g
	// Regex breakdown:
	// 1. <document[^>]*id="([^"]*)"[^>]*(\/?)>: Matches tags with an id attribute. Captures id in group 1.
	// 2. <document[^>]*(\/?)>: Matches tags without an id attribute (or if the first part didn't match).

	// Remove backticks first, as they can interfere with tag parsing
	if (processedMessage.includes('`')) {
		processedMessage = processedMessage.replace(/`/g, '')
	}

	processedMessage = replaceLooseLawBracketCitationsWithLinks(
		processedMessage,
		payloads,
		replaceFunctions,
		idsFoundInDocTags,
	)

	processedMessage = replaceLooseLawIdBracketCitationsWithLinks(
		processedMessage,
		payloads,
		replaceFunctions,
		idsFoundInDocTags,
	)

	processedMessage = processedMessage.replace(documentRegex, docTag => {
		// Try to extract ID from the specific docTag match
		const idMatch = /id="([^"]*)"/.exec(docTag)
		const documentId = idMatch?.[1]

		if (documentId !== undefined && documentId !== null) {
			// documentId can be an empty string if id=""
			idsFoundInDocTags.push(documentId)
			const replacementLink = getReplacementForDocumentId(documentId, payloads, replaceFunctions)
			// If id="", and not found, original code implies it becomes empty string.
			if (documentId === '' && !replacementLink) return ''
			return replacementLink || documentId // If not found in payload, use the ID itself
		} else {
			// No ID attribute found in the tag, or malformed
			return '*(Ver link adjunto)*'
		}
	})

	// --- Pass 2: Handle standalone IDs (ext-, sen-, act-) ---
	// This regex matches the described patterns.
	// It ensures they are whole words/tokens using \b word boundaries.
	const standaloneIdPattern = new RegExp(standaloneLegalDocumentIdPatternSource, 'g')

	processedMessage = processedMessage.replace(standaloneIdPattern, standaloneId => {
		// Check if this ID was already part of a <document> tag that got replaced.
		// This is a simple check. If the <document> tag was replaced by a link,
		// the raw ID might still be part of the link text or URL.
		// However, the goal is to not process it *again* as a standalone if it was *the ID of a tag*.
		// Since idsFoundInDocTags contains the raw IDs from those tags, we can check against it.
		// But this is not perfect. A better way is that Pass 1 replaces tags, so they are gone.
		// The current standaloneId is what's left in the text.

		const replacementLink = getReplacementForDocumentId(standaloneId, payloads, replaceFunctions)

		if (replacementLink) {
			idsFoundStandalone.push(standaloneId) // Record only if successfully replaced from payload
			return replacementLink
		}
		return standaloneId // If not found in payload, keep the ID text itself
	})

	// --- Combine found IDs ---
	// Collect all unique IDs that were successfully processed or were part of a processed tag.
	// Order is not important.
	const allProcessedIds = new Set<string>()

	idsFoundInDocTags.forEach(id => allProcessedIds.add(id))
	idsFoundStandalone.forEach(id => allProcessedIds.add(id))

	return { text: processedMessage, foundIds: Array.from(allProcessedIds).filter(Boolean) }
}

export function getCleanLinesFromAiMesageResponse(message: string, payloads: ChatMessagePayload | null) {
	return message
		.split('\n')
		.map(line => line.trim())
		.map(line => line.replace('<document>', '').replace('</document>', ''))
		.map(line => replaceDocumentWithLinksInLines(line, payloads))
}
